import { Box, Button, CloseButton, HStack, Text, Textarea } from "@chakra-ui/react";
import { ThumbsUp, ThumbsDown } from "@phosphor-icons/react";
import { colors } from "theme/foundations/colors";
import { useState, useEffect } from "react";
import { useToast } from "@chakra-ui/react";
import { postFeedback } from "api/ai/ai.service";
import { showSuccessToast } from "utils";
import { ChatMessage } from "api/ai/ai.service";
import { getFeedback } from "api/ai/ai.service";
import { useQuery } from "react-query";
import assert from "utils/assert";
/**
 *
 * Note: the comment is a separate widget for the caller to handle layout. Which is why feedback is passed in as a prop, so it can be shared between the two widgets.
 * Calling the API is handled inside this widget.
 *
 * @param {Object} props
 * @param {string} props.threadId required for feedback
 * @param {ChatMessage} props.message message.trace_id is preferred
 * @param {function} props.setShowFeedbackComment
 * @returns
 */
export function AIFeedbackThumbsUpDown({ threadId, message, feedback, setFeedback, setShowFeedbackComment }) {
  assert(setFeedback && setShowFeedbackComment);
  useEffect(() => {
    if (!feedback) {
      setFeedback({}); //undefined paranoia
    }
  }, [feedback, setFeedback]);

  // // fetch feedback from server
  const enabled = !!(threadId && (message?.id || message?.trace_id));
  const { data: fetchedFeedback } = useQuery({
    queryKey: ["feedback", threadId, message?.id || message?.trace_id],
    queryFn: () => getFeedback({ threadId, traceId: message?.trace_id, msgId: message?.id }),
    enabled,
    onSuccess: (fetchedFeedback) => {
      // console.log("fetched feedback", fetchedFeedback);
      setFeedback(fetchedFeedback);
    },
	onError: (error) => {
      console.warn("(swallow) error fetching feedback", error);
    },
  });

  const handleThumb = (message, positive) => {
    const newFeedback = Object.assign({}, feedback, { positive });
    setFeedback(newFeedback);
    console.log("handle thumb", message, newFeedback);
    setShowFeedbackComment(positive === false);
    postFeedback({ threadId, traceId: message.trace_id, msgId: message.id, feedback: newFeedback });
  };
  if (!enabled) {
    return null;
  }
  return (
    <HStack>
      <ThumbsUp
        size={24}
        weight={feedback?.positive ? "fill" : "regular"}
        color={colors.navIconLime}
        onClick={() => handleThumb(message, true)}
        cursor="pointer"
      />
      <ThumbsDown
        size={24}
        weight={feedback?.positive === false ? "fill" : "regular"}
        color={colors.navIconLime}
        onClick={() => handleThumb(message, false)}
        cursor="pointer"
      />
    </HStack>
  );
}

/**
 * Assume the thumbs up/down widget is in use and will fetch the feedback.
 * @param {*} param0
 * @returns
 */
export function AIFeedbackComment({
  threadId,
  message,
  feedback,
  setFeedback,
  showFeedbackComment,
  setShowFeedbackComment,
}) {
  assert(setFeedback && setShowFeedbackComment);
  if (!feedback) feedback = {}; //undefined paranoia (thumbs up/down widget should handle this)
  const [comment, setComment] = useState();
  useEffect(() => {
    setComment(feedback?.comment);
  }, [feedback?.comment]);
  // initialise widget visibility if the feedback has a comment
  useEffect(() => {
    setShowFeedbackComment(feedback?.positive === false && !!feedback?.comment);
  }, [feedback?.comment, feedback?.positive, setShowFeedbackComment]);

  const toast = useToast();

  /** @type {Feedback} */
  const handleFeedbackCommentSubmit = () => {
    const newFeedback = { ...feedback, comment: comment };
    setFeedback(newFeedback);
    console.log("handle feedback comment submit", newFeedback);
    postFeedback({ threadId, traceId: message.trace_id, msgId: message.id, feedback: newFeedback }).then((data) => {
      showSuccessToast(toast, "Thank you for your feedback", "ai-feedback-comment-success");
    });
  };

  if (!threadId) {
    console.log("No threadId", threadId, message);
    return null;
  }
  if (feedback?.positive !== false && !showFeedbackComment) {
    // console.log("No showFeedbackComment with feedback:", JSON.stringify(feedback));
    return null;
  }
  return (
    <Box>
      <Text>What is wrong, or what could be improved?</Text>
      <Textarea
        placeholder="Add a comment..."
        mt={2}
        rows={2}
        value={comment}
        onChange={(e) => setComment(e.target.value)}
      />
      <HStack justify="flex-end">
        <Button
          variant="primary"
          size="sm"
          mt={2}
          isDisabled={!comment || comment === feedback?.comment}
          onClick={handleFeedbackCommentSubmit}
        >
          Submit
        </Button>
      </HStack>
    </Box>
  );
}
